import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
} from 'react-bootstrap';
import { useAuth } from '@context';
import { useEmailDeliveryConfiguration } from '@hooks';
import { isAdmin } from '@utils/permissions';

const formatDateTime = (value) => {
  if (!value) return 'Not saved yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
};

const createFormState = (config = {}, fallback = {}) => ({
  deliveryMode: config.deliveryMode || fallback.deliveryMode || 'log',
  webhookUrl: config.webhookUrl || fallback.webhookUrl || '',
  webhookAuthHeader: config.webhookAuthHeader || fallback.webhookAuthHeader || 'Authorization',
  webhookAuthToken: config.webhookAuthToken || fallback.webhookAuthToken || '',
  resendApiKey: config.resendApiKey || fallback.resendApiKey || '',
  postmarkServerToken: config.postmarkServerToken || fallback.postmarkServerToken || '',
  postmarkMessageStream: config.postmarkMessageStream || fallback.postmarkMessageStream || 'outbound',
  smtpHost: config.smtpHost || fallback.smtpHost || '',
  smtpPort: String(config.smtpPort || fallback.smtpPort || 587),
  smtpSecure: typeof config.smtpSecure === 'boolean'
    ? config.smtpSecure
    : (typeof fallback.smtpSecure === 'boolean' ? fallback.smtpSecure : false),
  smtpUsername: config.smtpUsername || fallback.smtpUsername || '',
  smtpPassword: config.smtpPassword || fallback.smtpPassword || '',
  fromAddress: config.fromAddress || fallback.fromAddress || 'no-reply@adaptive-learning.local',
  appName: config.appName || fallback.appName || 'Adaptive Learning',
  supportEmail: config.supportEmail || fallback.supportEmail || 'support@adaptive-learning.local',
  managerName: config.managerName || fallback.managerName || 'Learning Team',
  frontendBaseUrl: config.frontendBaseUrl || fallback.frontendBaseUrl || 'https://example.com',
  loginUrl: config.loginUrl || fallback.loginUrl || '',
  reminderIdleDays: String(config.reminderIdleDays || fallback.reminderIdleDays || 3),
  reminderMaxRecipients: String(config.reminderMaxRecipients || fallback.reminderMaxRecipients || 100),
});

const toPositiveInt = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }
  return parsed;
};

export function EmailConfigurationPage() {
  const { user } = useAuth();
  const userIsAdmin = isAdmin(user);

  const {
    config,
    defaults,
    deliveryModes,
    hasPersistedOverride,
    updatedAt,
    loading,
    saving,
    error,
    refresh,
    saveConfiguration,
    resetConfiguration,
    clearError,
  } = useEmailDeliveryConfiguration(userIsAdmin);

  const [formState, setFormState] = useState(() => createFormState(config, defaults));
  const [successMessage, setSuccessMessage] = useState(null);

  const baselineFormState = useMemo(
    () => createFormState(config, defaults),
    [config, defaults]
  );

  useEffect(() => {
    setFormState(baselineFormState);
  }, [baselineFormState]);

  const isDirty = useMemo(
    () => JSON.stringify(formState) !== JSON.stringify(baselineFormState),
    [baselineFormState, formState]
  );

  const isWebhookMode = formState.deliveryMode === 'webhook';
  const isResendMode = formState.deliveryMode === 'resend';
  const isPostmarkMode = formState.deliveryMode === 'postmark';
  const isSmtpMode = formState.deliveryMode === 'smtp';

  const missingWebhookUrl = isWebhookMode && !formState.webhookUrl.trim();
  const missingResendApiKey = isResendMode && !formState.resendApiKey.trim();
  const missingPostmarkToken = isPostmarkMode && !formState.postmarkServerToken.trim();
  const missingSmtpHost = isSmtpMode && !formState.smtpHost.trim();
  const invalidSmtpCredentials = isSmtpMode
    && Boolean(formState.smtpUsername.trim()) !== Boolean(formState.smtpPassword.trim());
  const hasModeValidationError = (
    missingWebhookUrl
    || missingResendApiKey
    || missingPostmarkToken
    || missingSmtpHost
    || invalidSmtpCredentials
  );

  const handleFieldChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!userIsAdmin) {
      return;
    }

    try {
      await saveConfiguration({
        ...formState,
        smtpPort: toPositiveInt(formState.smtpPort, Number(defaults.smtpPort) || 587),
        smtpSecure: Boolean(formState.smtpSecure),
        reminderIdleDays: toPositiveInt(formState.reminderIdleDays, Number(defaults.reminderIdleDays) || 3),
        reminderMaxRecipients: toPositiveInt(
          formState.reminderMaxRecipients,
          Number(defaults.reminderMaxRecipients) || 100
        ),
      });

      setSuccessMessage('Email configuration saved successfully.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      // Error state is exposed by the hook.
    }
  };

  const handleResetOverrides = async () => {
    if (!userIsAdmin) {
      return;
    }

    const confirmed = window.confirm(
      'Reset all email configuration overrides and fall back to environment defaults?'
    );

    if (!confirmed) {
      return;
    }

    try {
      await resetConfiguration();
      setSuccessMessage('Email configuration overrides were reset to environment defaults.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch {
      // Error state is exposed by the hook.
    }
  };

  const disableEditing = loading || saving || !userIsAdmin;

  if (!userIsAdmin) {
    return (
      <div>
        <div className="mb-4">
          <h4 className="mb-1">Email Configuration</h4>
          <small className="text-muted">
            Manage global delivery settings, sender values, and reminder defaults used by course email templates.
          </small>
        </div>

        <Alert variant="danger" className="mb-0">
          Only administrators can access global email configuration.
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h4 className="mb-1">Email Configuration</h4>
        <small className="text-muted">
          Manage global delivery settings, sender values, and reminder defaults used by course email templates.
        </small>
      </div>

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={clearError}>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <div className="small text-muted">Configuration Source</div>
              <div>{hasPersistedOverride ? 'Database override' : 'Environment defaults'}</div>
            </Col>
            <Col md={4}>
              <div className="small text-muted">Last Updated</div>
              <div>{formatDateTime(updatedAt)}</div>
            </Col>
            <Col md={4} className="d-flex gap-2 justify-content-md-end align-items-end">
              <Button variant="outline-secondary" onClick={refresh} disabled={loading || saving}>
                Reload
              </Button>
              <Button
                variant="outline-danger"
                onClick={handleResetOverrides}
                disabled={disableEditing || !hasPersistedOverride}
              >
                Reset Overrides
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>
          <strong>Delivery Mode</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Delivery Mode</Form.Label>
                <Form.Select
                  value={formState.deliveryMode}
                  onChange={(event) => handleFieldChange('deliveryMode', event.target.value)}
                  disabled={disableEditing}
                >
                  {(deliveryModes || []).map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Use `resend`, `postmark`, `smtp`, or `webhook` for live delivery, `log` to simulate, and `disabled` to suppress sends.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>From Address</Form.Label>
                <Form.Control
                  type="email"
                  value={formState.fromAddress}
                  onChange={(event) => handleFieldChange('fromAddress', event.target.value)}
                  disabled={disableEditing}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Application Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formState.appName}
                  onChange={(event) => handleFieldChange('appName', event.target.value)}
                  disabled={disableEditing}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {isWebhookMode && (
        <Card className="mb-3">
          <Card.Header>
            <strong>Webhook Delivery Settings</strong>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Webhook URL</Form.Label>
                  <Form.Control
                    type="url"
                    value={formState.webhookUrl}
                    onChange={(event) => handleFieldChange('webhookUrl', event.target.value)}
                    placeholder="https://mail-provider.example/send"
                    disabled={disableEditing}
                  />
                  {missingWebhookUrl && (
                    <Form.Text className="text-danger">
                      A webhook URL is required when delivery mode is set to webhook.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Auth Header</Form.Label>
                  <Form.Control
                    type="text"
                    value={formState.webhookAuthHeader}
                    onChange={(event) => handleFieldChange('webhookAuthHeader', event.target.value)}
                    disabled={disableEditing}
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Auth Token</Form.Label>
                  <Form.Control
                    type="password"
                    value={formState.webhookAuthToken}
                    onChange={(event) => handleFieldChange('webhookAuthToken', event.target.value)}
                    disabled={disableEditing}
                    placeholder="Optional"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {isResendMode && (
        <Card className="mb-3">
          <Card.Header>
            <strong>Resend Delivery Settings</strong>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Resend API Key</Form.Label>
                  <Form.Control
                    type="password"
                    value={formState.resendApiKey}
                    onChange={(event) => handleFieldChange('resendApiKey', event.target.value)}
                    placeholder="re_..."
                    disabled={disableEditing}
                  />
                  {missingResendApiKey && (
                    <Form.Text className="text-danger">
                      A Resend API key is required when delivery mode is set to resend.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {isPostmarkMode && (
        <Card className="mb-3">
          <Card.Header>
            <strong>Postmark Delivery Settings</strong>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Server Token</Form.Label>
                  <Form.Control
                    type="password"
                    value={formState.postmarkServerToken}
                    onChange={(event) => handleFieldChange('postmarkServerToken', event.target.value)}
                    placeholder="pm_..."
                    disabled={disableEditing}
                  />
                  {missingPostmarkToken && (
                    <Form.Text className="text-danger">
                      A Postmark server token is required when delivery mode is set to postmark.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Message Stream</Form.Label>
                  <Form.Control
                    type="text"
                    value={formState.postmarkMessageStream}
                    onChange={(event) => handleFieldChange('postmarkMessageStream', event.target.value)}
                    placeholder="outbound"
                    disabled={disableEditing}
                  />
                  <Form.Text className="text-muted">
                    Defaults to `outbound`.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {isSmtpMode && (
        <Card className="mb-3">
          <Card.Header>
            <strong>SMTP Delivery Settings</strong>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>SMTP Host</Form.Label>
                  <Form.Control
                    type="text"
                    value={formState.smtpHost}
                    onChange={(event) => handleFieldChange('smtpHost', event.target.value)}
                    placeholder="smtp.example.com"
                    disabled={disableEditing}
                  />
                  {missingSmtpHost && (
                    <Form.Text className="text-danger">
                      SMTP host is required when delivery mode is set to smtp.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={2}>
                <Form.Group>
                  <Form.Label>SMTP Port</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={65535}
                    value={formState.smtpPort}
                    onChange={(event) => handleFieldChange('smtpPort', event.target.value)}
                    disabled={disableEditing}
                  />
                </Form.Group>
              </Col>

              <Col md={2} className="d-flex align-items-end">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="email-config-smtp-secure"
                    label="Use TLS/SSL"
                    checked={Boolean(formState.smtpSecure)}
                    onChange={(event) => handleFieldChange('smtpSecure', event.target.checked)}
                    disabled={disableEditing}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>SMTP Username</Form.Label>
                  <Form.Control
                    type="text"
                    value={formState.smtpUsername}
                    onChange={(event) => handleFieldChange('smtpUsername', event.target.value)}
                    placeholder="Optional"
                    disabled={disableEditing}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>SMTP Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={formState.smtpPassword}
                    onChange={(event) => handleFieldChange('smtpPassword', event.target.value)}
                    placeholder="Optional"
                    disabled={disableEditing}
                  />
                  {invalidSmtpCredentials && (
                    <Form.Text className="text-danger">
                      Provide both SMTP username and password, or leave both empty.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card className="mb-3">
        <Card.Header>
          <strong>Template Variables and Links</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Support Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formState.supportEmail}
                  onChange={(event) => handleFieldChange('supportEmail', event.target.value)}
                  disabled={disableEditing}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Manager Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formState.managerName}
                  onChange={(event) => handleFieldChange('managerName', event.target.value)}
                  disabled={disableEditing}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Frontend Base URL</Form.Label>
                <Form.Control
                  type="url"
                  value={formState.frontendBaseUrl}
                  onChange={(event) => handleFieldChange('frontendBaseUrl', event.target.value)}
                  disabled={disableEditing}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Login URL Override</Form.Label>
                <Form.Control
                  type="url"
                  value={formState.loginUrl}
                  onChange={(event) => handleFieldChange('loginUrl', event.target.value)}
                  placeholder="Leave blank to use <frontend-base-url>/login"
                  disabled={disableEditing}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <strong>Study Reminder Defaults</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Idle Days Threshold</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={365}
                  value={formState.reminderIdleDays}
                  onChange={(event) => handleFieldChange('reminderIdleDays', event.target.value)}
                  disabled={disableEditing}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Max Reminder Recipients</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={2000}
                  value={formState.reminderMaxRecipients}
                  onChange={(event) => handleFieldChange('reminderMaxRecipients', event.target.value)}
                  disabled={disableEditing}
                />
              </Form.Group>
            </Col>

            <Col md={4} className="d-flex align-items-end justify-content-md-end">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={disableEditing || !isDirty || hasModeValidationError}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
}
